import {
  HELP_MANAGER_DETAILS,
  NAVIGATION_FROM_URL_PARAM,
  NAVIGATION_SOURCE_HQ,
} from '@/constants/Home';

/**
 * Validate if the email is valid
 * @param value - value to check
 */
export const validateEmail = (value: string): boolean => {
  const localPart = '([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)';
  const quotedPart = '(".+")';
  const domainPart =
    '((\\[d{1,3}\\.d{1,3}\\.d{1,3}\\.d{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))';

  const regexString = `^(${localPart}|${quotedPart})@${domainPart}$`;
  const emailRegex = new RegExp(regexString);

  return emailRegex.test(value);
};

/** Validate the phone number with our standard phone number regex
 * @param number - phone number to check
 */
export const validatePhoneNumber = (number: string) => {
  const phoneNumberPattern = /^(?:\+?1[-. ]?)?\(?([2-9]\d\d)\)?[-. ]?(\d{3})[-. ]?(\d{4})$/;
  return phoneNumberPattern.test(number);
};

/** Validate the name with our standard pattern
 * @param name - name to check
 */
export const validateName = (name: string) => {
  const words = name ? name.split(' ') : [];
  const valErr = words.filter(Boolean).length >= 2 ? 1 : 0;
  return !!valErr;
};

export const fullNameRegex = /^\D+$/;

/** To check if string contains only spaces */
export const onlySpaceRegex = /^\s*$/;

/**
 * Checks if the item is in the list
 * @param list - list of items
 * @param item - item to check
 */
export const isInList = (list: string[], item: string): boolean => list.includes(item);

/**
 * Appends the navigation source query param so the destination app can track where the user came from.
 *
 * @param url - URL to navigate to
 * @param source - Source page identifier (defaults to HQ)
 */
const mergeHqFromUrlParam = (url: string, pageUrl: string, fromUrlSuffix: string): string => {
  if (/[?&]fromUrl=/.test(url)) {
    return url.replace(
      new RegExp(`([?&]${NAVIGATION_FROM_URL_PARAM}=)[^&]*`),
      `$1${encodeURIComponent(pageUrl)}`
    );
  }

  return `${url}${fromUrlSuffix}`;
};

const appendHqOutboundQueryParams = (url: string, source: string, hqPageUrl?: string): string => {
  const encodedSource = encodeURIComponent(source);
  const fromUrlSuffix =
    hqPageUrl !== undefined ? `&${NAVIGATION_FROM_URL_PARAM}=${encodeURIComponent(hqPageUrl)}` : '';

  if (/[?&]source=/.test(url)) {
    let result = url.replace(/([?&]source=)[^&]*/, `$1${encodedSource}`);
    if (hqPageUrl !== undefined) {
      result = mergeHqFromUrlParam(result, hqPageUrl, fromUrlSuffix);
    }
    return result;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}source=${encodedSource}${fromUrlSuffix}`;
};

export const appendSourceQueryParam = (
  url: string,
  source: string = NAVIGATION_SOURCE_HQ
): string => {
  const hqPageUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}${window.location.search}`
      : undefined;

  try {
    const parsedUrl = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : undefined
    );
    parsedUrl.searchParams.set('source', source);
    if (hqPageUrl !== undefined) {
      parsedUrl.searchParams.set(NAVIGATION_FROM_URL_PARAM, hqPageUrl);
    }
    return parsedUrl.href;
  } catch {
    return appendHqOutboundQueryParams(url, source, hqPageUrl);
  }
};

/** Ensures cross-app analytics source is stamped on any HQ outbound URL. */
export const finalizeHqOutboundUrl = (url: string, source: string = NAVIGATION_SOURCE_HQ): string =>
  appendSourceQueryParam(url, source);

/**
 * Method to open external link
 * @param url - url to open
 * @param target - target to open the url (default: _self)
 */
export const openExternalLink = (url: string, target = '_self') => {
  window.open(url, target);
};

/**
 * Opens an external link from HQ with the source query param for analytics tracking.
 *
 * @param url - url to open
 * @param target - target to open the url (default: _self)
 */
export const openHqExternalLink = (url: string, target = '_self') => {
  window.open(appendSourceQueryParam(url), target);
};

/**
 * Method to transform phone number into display and href format
 * @param phone - phone number to transform
 */
export const transformPhone = (phone: string): { displayNumber: string; hrefNumber: string } => {
  if (!phone) return HELP_MANAGER_DETAILS.phone;

  const phoneObj = {
    displayNumber: '',
    hrefNumber: '',
  };

  try {
    /** Extracting all digits from the string - req. to exclude ()+- */
    let allDigits = phone.replace(/\D+/g, '');

    /** Formatting number into US format if the no of digits is 10 or 11 */
    if ([10, 11].includes(allDigits.length)) {
      let intlCode = '';

      /** International Code */
      if (allDigits.length === 11) {
        intlCode = `+${allDigits.charAt(0)} `;
        allDigits = allDigits.slice(1);
      }

      /** Matching the regex for req format: (988) 589-2254 */
      const regMatch = /^(\d{3})(\d{3})(\d{4})$/.exec(allDigits) as RegExpMatchArray;

      phoneObj.displayNumber = `${intlCode}(${regMatch[1]}) ${regMatch[2]}-${regMatch[3]}`;
      phoneObj.hrefNumber = `${intlCode.length ? intlCode : '+1'}${regMatch[1]}${regMatch[2]}${
        regMatch[3]
      }`;

      return phoneObj;
    }
    /** Throwing an error when number is shorter or lengthier */
    throw new Error('Number is not in proper phone format');
  } catch (error) {
    /** Returning the fallback string */
    return HELP_MANAGER_DETAILS.phone;
  }
};

/**
 * Function to get only numbers from a string
 * @param value - value to check
 */
export const getOnlyNumbers = (value: string) => parseInt(value.replace(/\D/g, ''), 10);
